import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import PublicNav from "@/components/PublicNav";
import { Search, ShoppingCart, X, Loader2, CheckCircle2, Copy, Download, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["All", "Gaming", "Streaming", "Shopping", "Phone Credit", "Food", "Finance"];


const COUNTRY_NAMES = {
    us: "United States", eu: "Europe", ww: "Worldwide", uk: "United Kingdom",
    de: "Germany", au: "Australia", pl: "Poland", fr: "France", nl: "Netherlands",
    ca: "Canada", be: "Belgium", at: "Austria", no: "Norway", in: "India",
    it: "Italy", se: "Sweden", es: "Spain", dk: "Denmark", br: "Brazil",
    ch: "Switzerland", fi: "Finland", pt: "Portugal", cz: "Czechia", ro: "Romania",
    mx: "Mexico", ie: "Ireland", hu: "Hungary", ae: "UAE", nz: "New Zealand",
    za: "South Africa", tr: "Turkey", sk: "Slovakia", jp: "Japan", ph: "Philippines",
    sa: "Saudi Arabia", th: "Thailand", hr: "Croatia", gr: "Greece", sg: "Singapore",
    my: "Malaysia", vn: "Vietnam", pe: "Peru", id: "Indonesia", cn: "China",
    ar: "Argentina", cl: "Chile", tw: "Taiwan", hk: "Hong Kong", ru: "Russia",
};
const countryLabel = (c) => COUNTRY_NAMES[(c || "").toLowerCase()] || (c || "").toUpperCase();

const CATEGORY_KEYWORDS = {
    "Gaming": ["xbox", "playstation", "steam", "nintendo", "razer", "gaming", "game", "roblox", "minecraft", "fortnite", "blizzard", "ea ", "ubisoft"],
    "Streaming": ["netflix", "spotify", "disney", "hulu", "youtube", "deezer", "tidal", "apple", "itunes", "amazon prime", "hbo"],
    "Shopping": ["amazon", "walmart", "ebay", "zalando", "bol", "coolblue", "ikea", "shein", "aliexpress", "wish"],
    "Phone Credit": ["top up", "topup", "mobile", "vodafone", "orange", "t-mobile", "3g", "lebara", "lycamobile", "airtime", "sim"],
    "Food": ["doordash", "uber eats", "deliveroo", "just eat", "dominos", "starbucks", "mcdonalds", "pizza", "food"],
    "Finance": ["paypal", "visa", "mastercard", "paysafe", "neosurf", "prepaid", "credit", "mifinity"],
};

function ProductCard({ product, onSelect }) {
    const imgSrc = product.imageUrl || "";
    return (
        <div onClick={() => onSelect(product)}
            className="bg-white rounded-2xl border border-slate-100 shadow-card p-4 hover:shadow-floating hover:border-emerald-200 transition-all cursor-pointer">
            <div className="aspect-video rounded-xl overflow-hidden bg-slate-50 mb-3 flex items-center justify-center">
                {imgSrc ? (
                    <img src={imgSrc} alt={product.name} className="w-full h-full object-contain p-2" loading="lazy" />
                ) : (
                    <div className="text-3xl">🎁</div>
                )}
            </div>
            <div className="font-semibold text-slate-900 text-sm truncate">{product.name}</div>
            <div className="text-xs text-slate-500 mt-1">{product.brand}</div>
            <div className="mt-2 flex items-center justify-between">
                <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Gift Card</span>
                <span className="text-xs text-slate-400">{product.currencyCode}</span>
            </div>
        </div>
    );
}

function StockModal({ product, onClose, onBuy, onAddToCart }) {
    const [stocks, setStocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [payMethod, setPayMethod] = useState("wallet");
    const [buying, setBuying] = useState(false);
    const [error, setError] = useState("");
    const { user } = useAuth();
    const { fmt, currency } = useCurrency();
    const navigate = useNavigate();

    useEffect(() => {
        api.post("/prepaidforge/stocks", { skus: [product.sku] })
            .then(r => { setStocks(r.data); setLoading(false); })
            .catch(() => { setError("Failed to load stock"); setLoading(false); });
    }, [product.sku]);

    const handleBuy = async () => {
        if (!user) { navigate("/login?redirect=/buy-giftcard"); return; }
        if (!selected) return;
        setBuying(true); setError("");
        api.post("/track/buygiftcard", { event: "buy_click" }).catch(() => {});
        try {
            if (payMethod === "stripe") {
                const result = await api.post("/prepaidforge/stripe-checkout", {
                    sku: product.sku, price_eur: selected.purchasePrice, currency,
                    code_type: selected.type,
                    success_url: window.location.origin + "/buy-giftcard?success=1",
                    cancel_url: window.location.origin + "/buy-giftcard",
                });
                window.location.href = result.data.checkout_url;
            } else if (payMethod === "paypal") {
                const result = await api.post("/prepaidforge/paypal-checkout", {
                    sku: product.sku, price_eur: selected.purchasePrice, code_type: selected.type,
                });
                window.location.href = result.data.checkout_url;
            } else if (payMethod === "etransfer") {
                const result = await api.post("/prepaidforge/etransfer-order", {
                    sku: product.sku, price: selected.purchasePrice,
                    code_type: selected.type, payment_method: "etransfer",
                });
                onBuy({ ...result.data, etransfer: true });
            } else {
                const result = await api.post("/prepaidforge/order", {
                    sku: product.sku, price: selected.purchasePrice,
                    code_type: selected.type, payment_method: payMethod,
                });
                onBuy(result.data);
            }
        } catch (err) {
            setError(err.response?.data?.detail || "Order failed");
        } finally { setBuying(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                        {product.imageUrl && <img src={product.imageUrl} alt={product.name} className="w-10 h-10 object-contain rounded-lg bg-slate-50 p-1" />}
                        <div>
                            <h2 className="font-bold text-slate-900">{product.name}</h2>
                            <p className="text-xs text-slate-500">{product.brand}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 size={24} className="animate-spin text-emerald-500" />
                        </div>
                    ) : stocks.length === 0 ? (
                        <p className="text-center text-slate-500 py-8">No stock available</p>
                    ) : (
                        <>
                            <div>
                                <label className="text-xs font-semibold text-slate-600 mb-2 block">Select denomination</label>
                                <div className="space-y-2">
                                    {stocks.filter(s => s.quantity !== 0).map((s, i) => (
                                        <button key={i} onClick={() => setSelected(s)}
                                            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all ${selected === s ? "border-emerald-500 bg-emerald-50" : "border-slate-100 hover:border-slate-200"}`}>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{s.type}</span>
                                                <span className="text-sm font-semibold text-slate-900">{fmt(s.purchasePrice)}</span>
                                            </div>
                                            <span className="text-xs text-slate-500">{s.quantity === -1 ? "In stock" : `${s.quantity} left`}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-semibold text-slate-600 mb-2 block">Payment method</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        { id: "wallet", label: "Wallet" },
                                        { id: "stripe", label: "Credit / Debit" },
                                        { id: "paypal", label: "PayPal" },
                                        { id: "etransfer", label: "E-Transfer (CA)" },
                                    ].map(m => (
                                        <button key={m.id} onClick={() => setPayMethod(m.id)}
                                            className={`py-2 px-3 rounded-xl border-2 text-xs font-semibold transition-all ${payMethod === m.id ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-100 text-slate-600 hover:border-slate-200"}`}>
                                            {m.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {!user && (
                                <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                                    You need to <Link to="/login?redirect=/buy-giftcard" className="underline font-semibold">login</Link> to purchase
                                </p>
                            )}

                            {error && <p className="text-sm text-rose-600 bg-rose-50 px-3 py-2 rounded-lg">{error}</p>}

                            <div className="grid grid-cols-2 gap-2">
                                <button onClick={() => { if (selected) { onAddToCart(product, selected); onClose(); } }}
                                    disabled={!selected}
                                    className="bg-slate-100 hover:bg-slate-200 disabled:opacity-40 text-slate-700 font-bold py-3 rounded-xl flex items-center justify-center gap-2 text-sm">
                                    <Plus size={14} /> Add to Cart
                                </button>
                                <button onClick={handleBuy} disabled={!selected || buying}
                                    className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 text-sm">
                                    {buying ? <Loader2 size={14} className="animate-spin" /> : <ShoppingCart size={14} />}
                                    {buying ? "..." : selected ? `Buy ${fmt(selected.purchasePrice)}` : "Select"}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function OrderSuccessModal({ order, onClose }) {
    const [copied, setCopied] = useState(false);
    const copyCode = () => {
        if (order.code) { navigator.clipboard.writeText(order.code); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 size={32} className="text-emerald-500" />
                </div>
                {order.etransfer ? (
                    <>
                        <h2 className="text-xl font-bold text-slate-900 mb-1">Almost There!</h2>
                        <p className="text-sm text-slate-500 mb-4">Send your Interac E-Transfer to complete the order</p>
                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4 text-left">
                            <p className="text-xs text-blue-600 font-semibold mb-2">E-Transfer Instructions</p>
                            <p className="text-sm text-slate-700 mb-1">Send <strong>CA${Number(order.price_cad||0).toFixed(2)}</strong> to:</p>
                            <p className="text-base font-bold text-slate-900 mb-2">support@gifttocash.com</p>
                            <p className="text-sm text-slate-700 mb-1">Reference/Message:</p>
                            <code className="text-sm font-bold text-blue-700">{order.order_ref || order.id}</code>
                        </div>
                        <p className="text-xs text-slate-400 mb-4">Your gift card code will be emailed within 30 minutes.</p>
                    </>
                ) : (
                    <>
                        <h2 className="text-xl font-bold text-slate-900 mb-1">Order Complete!</h2>
                        <p className="text-sm text-slate-500 mb-5">Your gift card has been delivered</p>
                        {order.code && (
                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4">
                                <p className="text-xs text-slate-500 mb-2">Your code</p>
                                <div className="flex items-center justify-between gap-3">
                                    <code className="text-lg font-bold text-slate-900 tracking-wider">{order.code}</code>
                                    <button onClick={copyCode} className="text-emerald-600">
                                        {copied ? <CheckCircle2 size={18} /> : <Copy size={18} />}
                                    </button>
                                </div>
                            </div>
                        )}
                        {order.image?.downloadLink && (
                            <a href={order.image.downloadLink} target="_blank" rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 text-sm font-semibold text-emerald-600 bg-emerald-50 px-4 py-3 rounded-xl mb-4">
                                <Download size={16} /> Download Card Image
                            </a>
                        )}
                        <p className="text-xs text-slate-400 mb-4">A copy has been sent to your email.</p>
                    </>
                )}
                <button onClick={onClose} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-xl">Done</button>
            </div>
        </div>
    );
}

export default function BuyGiftCard() {
    useEffect(() => {
        api.post("/track/buygiftcard", { event: "visit" }).catch(() => {});
    }, []);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("All");
    const [country, setCountry] = useState("All");
    const [curFilter, setCurFilter] = useState("All");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(75);
    const [selected, setSelected] = useState(null);
    const [orderResult, setOrderResult] = useState(null);
    const [cart, setCart] = useState(() => {
        try { return JSON.parse(localStorage.getItem("gtc_cart") || "[]"); } catch { return []; }
    });

    const cartCount = cart.reduce((sum, i) => sum + i.qty, 0);

    // Load FULL catalog once; search + categories filter the whole set
    useEffect(() => {
        setLoading(true);
        api.get("/prepaidforge/products/all")
            .then(r => { setProducts(r.data || []); setLoading(false); })
            .catch(err => { setError(err.response?.data?.detail || "Failed to load products"); setLoading(false); });
    }, []);
    useEffect(() => { setPage(1); }, [search, category, country, curFilter]);

    const addToCart = (product, stock) => {
        const existing = cart.find(i => i.sku === product.sku && i.stock.purchasePrice === stock.purchasePrice);
        const newCart = existing
            ? cart.map(i => i.sku === product.sku && i.stock.purchasePrice === stock.purchasePrice ? { ...i, qty: i.qty + 1 } : i)
            : [...cart, { sku: product.sku, name: product.name, image: product.imageUrl, stock, qty: 1 }];
        setCart(newCart);
        localStorage.setItem("gtc_cart", JSON.stringify(newCart));
    };

    const PAGE_SIZE = 24;
    const countryOptions = useMemo(() => {
        const set = new Map();
        products.forEach(p => (p.countries || []).forEach(c => {
            const k = (c || "").toLowerCase(); if (k) set.set(k, (set.get(k) || 0) + 1);
        }));
        return [...set.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c);
    }, [products]);
    const currencyOptions = useMemo(() => {
        const set = new Map();
        products.forEach(p => { const c = p.currencyCode; if (c) set.set(c, (set.get(c) || 0) + 1); });
        return [...set.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c);
    }, [products]);
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return products
            .filter(p => p.active !== false)
            .filter(p => {
                if (category === "All") return true;
                // Prefer PrepaidForge's real category field; fall back to keyword match
                const cats = (p.category || []).map(c => (c || "").toLowerCase());
                const keywords = CATEGORY_KEYWORDS[category] || [];
                const catLower = category.toLowerCase();
                if (cats.some(c => c === catLower || keywords.includes(c))) return true;
                const name = (p.name || "").toLowerCase();
                const brand = (p.brand || "").toLowerCase();
                return keywords.some(k => name.includes(k) || brand.includes(k) || cats.some(c => c.includes(k)));
            })
            .filter(p => country === "All" || (p.countries || []).some(c => (c || "").toLowerCase() === country))
            .filter(p => curFilter === "All" || p.currencyCode === curFilter)
            .filter(p => !q
                || (p.name || "").toLowerCase().includes(q)
                || (p.brand || "").toLowerCase().includes(q)
                || (p.sku || "").toLowerCase().includes(q));
    }, [products, category, search, country, curFilter]);

    const computedTotalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paged = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);
    const pageNums = useMemo(() => {
        const start = Math.max(1, page - 2);
        return Array.from({ length: 5 }, (_, i) => start + i).filter(n => n <= computedTotalPages);
    }, [page, computedTotalPages]);

    return (
        <div className="min-h-screen bg-slate-50">
            <PublicNav />
            {selected && !orderResult && (
                <StockModal product={selected} onClose={() => setSelected(null)}
                    onBuy={(r) => { setSelected(null); setOrderResult(r); }}
                    onAddToCart={addToCart} />
            )}
            {orderResult && <OrderSuccessModal order={orderResult} onClose={() => setOrderResult(null)} />}

            <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
                {/* Header */}
                <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-6 text-white flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold mb-1">Buy Gift Cards</h1>
                        <p className="text-emerald-100 text-sm">Instant delivery — 1000+ gift cards from top brands worldwide</p>
                    </div>
                    <Link to="/buy-giftcard/cart" className="relative bg-white/20 hover:bg-white/30 text-white font-semibold px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all">
                        <ShoppingCart size={18} />
                        <span>Cart{cartCount > 0 ? ` (${cartCount})` : ""}</span>
                        {cartCount > 0 && <span className="absolute -top-2 -right-2 w-5 h-5 bg-rose-500 text-white text-xs font-bold rounded-full flex items-center justify-center">{cartCount}</span>}
                    </Link>
                </div>

                {/* Search + Filter */}
                <div className="flex flex-wrap gap-3">
                    <div className="flex items-center bg-white border border-slate-200 rounded-full px-4 py-2.5 w-72 max-w-full">
                        <Search size={16} className="text-slate-400" />
                        <input value={search} onChange={e => setSearch(e.target.value)}
                            placeholder="Search gift cards..."
                            className="bg-transparent outline-none text-sm ml-2 flex-1 placeholder-slate-400" />
                    </div>
                    <select value={country} onChange={e => setCountry(e.target.value)}
                        className="bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm text-slate-600 outline-none cursor-pointer focus:border-emerald-300">
                        <option value="All">All Countries</option>
                        {countryOptions.map(c => <option key={c} value={c}>{countryLabel(c)}</option>)}
                    </select>
                    <select value={curFilter} onChange={e => setCurFilter(e.target.value)}
                        className="bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm text-slate-600 outline-none cursor-pointer focus:border-emerald-300">
                        <option value="All">All Currencies</option>
                        {currencyOptions.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <div className="flex gap-2 flex-wrap">
                        {CATEGORIES.map(c => (
                            <button key={c} onClick={() => setCategory(c)}
                                className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${category === c ? "bg-emerald-500 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-emerald-300"}`}>
                                {c}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Products */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 size={32} className="animate-spin text-emerald-500" />
                    </div>
                ) : error ? (
                    <div className="bg-white border border-slate-100 rounded-2xl p-16 text-center shadow-card">
                        <div className="text-6xl mb-4">🎁</div>
                        <h2 className="text-2xl font-bold text-slate-900 mb-2">Coming Soon</h2>
                        <p className="text-slate-500 text-sm max-w-md mx-auto mb-6">We are stocking up our gift card store with 1000+ cards from top brands. Check back very soon!</p>
                        <div className="flex flex-wrap justify-center gap-3 mb-8">
                            {["Amazon", "iTunes", "Netflix", "Xbox", "Google Play", "Steam", "PlayStation", "Spotify"].map(b => (
                                <span key={b} className="bg-slate-50 border border-slate-200 text-slate-600 text-xs font-medium px-3 py-1.5 rounded-full">{b}</span>
                            ))}
                        </div>
                        <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm font-semibold px-5 py-2.5 rounded-full">
                            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                            Launching very soon
                        </div>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center text-slate-500">
                        No products found{search ? ` for "${search}"` : ""}{country !== "All" ? ` in ${countryLabel(country)}` : ""}{curFilter !== "All" ? ` (${curFilter})` : ""}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                        {paged.map(p => <ProductCard key={p.sku} product={p} onSelect={setSelected} />)}
                    </div>
                )}

                {/* Pagination */}
                {!loading && !error && (
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                            className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-medium disabled:opacity-40">
                            Previous
                        </button>
                        {pageNums.map(pg => (
                            <button key={pg} onClick={() => setPage(pg)}
                                className={`w-9 h-9 rounded-full text-sm font-semibold ${pg === page ? "bg-emerald-500 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-emerald-300"}`}>
                                {pg}
                            </button>
                        ))}
                        <span className="text-xs text-slate-400 px-2">of {computedTotalPages} pages</span>
                        <button onClick={() => setPage(p => Math.min(computedTotalPages, p + 1))} disabled={page >= computedTotalPages}
                            className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-medium disabled:opacity-40">
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}